import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const CookiePolicy = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`relative w-full max-w-4xl max-h-[70vh] overflow-y-auto rounded-2xl shadow-2xl ${
        isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className="text-2xl font-bold">Cookie Policy</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isDark 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString()}
          </div>

          <section>
            <h3 className="text-lg font-semibold mb-2">What Are Cookies?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Cookies are small text files that are placed on your computer or mobile device when you 
              visit our website. They are widely used to make websites work more efficiently and to 
              provide information to website owners about how visitors use their site.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">How We Use Cookies</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-2">
              We use cookies for several purposes:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <li>To remember your preferences and settings</li>
              <li>To analyze how you use our website and improve its performance</li>
              <li>To provide personalized content and advertisements</li>
              <li>To ensure the security of our website</li>
              <li>To enable certain features and functionality</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Types of Cookies We Use</h3>
            
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-sm mb-1">Essential Cookies</h4>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  These cookies are necessary for the website to function properly. They enable basic 
                  functions like page navigation, access to secure areas, and remembering your 
                  preferences. The website cannot function properly without these cookies.
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-sm mb-1">Analytics Cookies</h4>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously. This helps us improve our website's performance 
                  and user experience.
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-sm mb-1">Functional Cookies</h4>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  These cookies enable the website to provide enhanced functionality and personalization. 
                  They may be set by us or by third-party providers whose services we have added to our pages.
                </p>
              </div>

              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h4 className="font-semibold text-sm mb-1">Marketing Cookies</h4>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  These cookies are used to track visitors across websites. The intention is to display 
                  ads that are relevant and engaging for individual users and thereby more valuable for 
                  publishers and third-party advertisers.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Third-Party Cookies</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              We may also use third-party cookies from trusted partners to help us analyze how our 
              website is used, provide social media features, and deliver relevant advertisements. 
              These third parties may use cookies to collect information about your online activities 
              across different websites.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Managing Cookies</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-2">
              You can control and manage cookies in several ways:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 dark:text-gray-300">
              <li>Use our cookie preference center (if available)</li>
              <li>Configure your browser settings to block or delete cookies</li>
              <li>Use browser extensions that block tracking cookies</li>
              <li>Opt out of specific third-party advertising networks</li>
            </ul>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mt-2">
              Please note that blocking certain cookies may affect the functionality of our website 
              and your user experience.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Cookie Retention</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              Different cookies have different retention periods. Session cookies are deleted when you 
              close your browser, while persistent cookies remain on your device for a set period or 
              until you delete them. We will not retain cookies longer than necessary for the purposes 
              for which they were collected.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold mb-2">Updates to This Policy</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for other operational, legal, or regulatory reasons. We will notify you of any 
              material changes by posting the updated policy on our website.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 p-6 border-t ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        }`}>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
